import React, { useRef, useEffect, useState } from 'react';
import styles from './styles.module.scss';

interface ZipCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  isValid: boolean | null;
  isChecking: boolean;
  onReset?: () => void;
}

/**
 * Componente per l'input del CAP con 5 caselle separate
 */
const ZipCodeInput: React.FC<ZipCodeInputProps> = ({
  value,
  onChange,
  isValid,
  isChecking,
  onReset,
}) => {
  // Inizializza con "2010" pre-compilato e "?" nella 5° casella
  const [digits, setDigits] = useState<string[]>(['2', '0', '1', '0', '?']);
  const [activeInputIndex, setActiveInputIndex] = useState<number>(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  console.log('ZipCodeInput - digits:', digits);
  console.log('ZipCodeInput - value:', value);

  // Notifica il valore iniziale "2010" al padre al mount
  useEffect(() => {
    if (!value) {
      onChange('2010');
    }
  }, []);

  // Sincronizza digits con value prop (mantieni "?" nella 5° casella se vuota)
  useEffect(() => {
    if (value) {
      const newDigits = value.padEnd(5, '').split('').slice(0, 5);
      // Se la 5° casella è vuota, mostra "?"
      if (!newDigits[4] || newDigits[4] === ' ') {
        newDigits[4] = '?';
      }
      setDigits(newDigits);
    }
  }, [value]);

  const handleDigitChange = (index: number, digitValue: string) => {
    // Permetti solo numeri
    if (digitValue && !/^\d$/.test(digitValue)) {
      return;
    }

    const newDigits = [...digits];
    // Per la 5° casella (index 4), se viene cancellato mostra "?"
    if (index === 4 && !digitValue) {
      newDigits[index] = '?';
    } else {
      newDigits[index] = digitValue;
    }
    setDigits(newDigits);

    // Aggiorna il valore completo (escludi il "?" dalla 5° casella)
    const newValue = newDigits.map(d => d === '?' ? '' : d).join('');
    onChange(newValue);

    // Auto-focus sul prossimo input se c'è un valore
    if (digitValue && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Se viene premuta una cifra, gestisci manualmente per garantire auto-avanzamento
    if (/^\d$/.test(e.key)) {
      e.preventDefault(); // Blocca il comportamento default del browser
      
      const newDigits = [...digits];
      newDigits[index] = e.key;
      setDigits(newDigits);
      // Passa solo i numeri al parent (escludi il "?")
      onChange(newDigits.map(d => d === '?' ? '' : d).join(''));
      
      // Auto-focus sul prossimo input
      if (index < 4) {
        inputRefs.current[index + 1]?.focus();
      }
      return;
    }
    
    if (e.key === 'Backspace') {
      // Per la 5° casella (index 4), se ha "?" vai indietro
      if ((digits[index] === '?' || !digits[index]) && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (digits[index] && digits[index] !== '?') {
        // Cancella il digit corrente
        const newDigits = [...digits];
        // Per la 5° casella, mostra "?" al posto di vuoto
        newDigits[index] = index === 4 ? '?' : '';
        setDigits(newDigits);
        onChange(newDigits.map(d => d === '?' ? '' : d).join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
    const newDigits = pastedData.padEnd(5, '').split('').slice(0, 5);
    setDigits(newDigits);
    onChange(pastedData);

    // Focus sull'ultimo digit inserito
    const lastIndex = Math.min(pastedData.length, 4);
    inputRefs.current[lastIndex]?.focus();
  };

  const handleFocus = (index: number) => {
    setActiveInputIndex(index);
    // Seleziona il contenuto quando viene focalizzato
    inputRefs.current[index]?.select();
  };

  const getInputBorderColor = (index: number) => {
    if (activeInputIndex === index) {
      return '#f59e0b'; // Orange for active
    }
    return '#d1d5db'; // Gray default
  };

  // Funzione per ottenere il colore del testo (rosso per "?", blu scuro per numeri)
  const getTextColor = (digit: string) => {
    return digit === '?' ? '#ef4444' : '#072543';
  };

  // Se il CAP è validato, mostra il layout finale con checkmark
  if (isValid === true && value.length === 5) {
    return (
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem 2rem',
        minHeight: '60px',
        border: '1px solid #d1d5db',
        borderRadius: '0.75rem',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          fontSize: '2.5rem',
          fontWeight: 700,
          color: '#072543',
        }}>
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              style={{
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                fontSize: '0.9rem',
                fontWeight: 500,
                cursor: 'pointer',
                padding: '0.25rem 0.5rem',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              ← Modifica CAP
            </button>
          )}
          <span style={{
            color: '#22c55e',
            fontSize: '2rem',
          }}>✓</span>
          {value}
        </div>
      </div>
    );
  }

  return (
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0',
        minHeight: '70px',
      }}>
      {/* Input boxes */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        justifyContent: 'center',
      }}>
        <input
          ref={(el) => (inputRefs.current[0] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className="cap-digit-input"
          value={digits[0]}
          onChange={(e) => handleDigitChange(0, e.target.value)}
          onKeyDown={(e) => handleKeyDown(0, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(0)}
          style={{
            width: '50px',
            height: '60px',
            border: `2px solid ${getInputBorderColor(0)}`,
            borderRadius: '0.5rem',
            fontSize: '1.5rem',
            textAlign: 'center' as const,
            backgroundColor: '#ffffff',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            color: '#072543',
            outline: 'none',
          }}
        />
        <input
          ref={(el) => (inputRefs.current[1] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className="cap-digit-input"
          value={digits[1]}
          onChange={(e) => handleDigitChange(1, e.target.value)}
          onKeyDown={(e) => handleKeyDown(1, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(1)}
          style={{
            width: '50px',
            height: '60px',
            border: `2px solid ${getInputBorderColor(1)}`,
            borderRadius: '0.5rem',
            fontSize: '1.5rem',
            textAlign: 'center' as const,
            backgroundColor: '#ffffff',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            color: '#072543',
            outline: 'none',
          }}
        />
        <input
          ref={(el) => (inputRefs.current[2] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className="cap-digit-input"
          value={digits[2]}
          onChange={(e) => handleDigitChange(2, e.target.value)}
          onKeyDown={(e) => handleKeyDown(2, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(2)}
          style={{
            width: '50px',
            height: '60px',
            border: `2px solid ${getInputBorderColor(2)}`,
            borderRadius: '0.5rem',
            fontSize: '1.5rem',
            textAlign: 'center' as const,
            backgroundColor: '#ffffff',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            color: '#072543',
            outline: 'none',
          }}
        />
        <input
          ref={(el) => (inputRefs.current[3] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className="cap-digit-input"
          value={digits[3]}
          onChange={(e) => handleDigitChange(3, e.target.value)}
          onKeyDown={(e) => handleKeyDown(3, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(3)}
          style={{
            width: '50px',
            height: '60px',
            border: `2px solid ${getInputBorderColor(3)}`,
            borderRadius: '0.5rem',
            fontSize: '1.5rem',
            textAlign: 'center' as const,
            backgroundColor: '#ffffff',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            color: '#072543',
            outline: 'none',
          }}
        />
        <input
          ref={(el) => (inputRefs.current[4] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className="cap-digit-input"
          value={digits[4]}
          onChange={(e) => handleDigitChange(4, e.target.value)}
          onKeyDown={(e) => handleKeyDown(4, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(4)}
          style={{
            width: '50px',
            height: '60px',
            border: `2px solid ${getInputBorderColor(4)}`,
            borderRadius: '0.5rem',
            fontSize: '1.5rem',
            textAlign: 'center' as const,
            backgroundColor: '#ffffff',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            color: getTextColor(digits[4]),
            outline: 'none',
          }}
        />
      </div>
    </div>
  );
};

export default ZipCodeInput;
